// Presentaciones de compra (caja, bolsa, bulto) en la compra desde foto y en
// la carga inicial — pos-graciela-api/docs/SPEC_PRESENTACIONES_EN_COMPRAS_v0.1.md
// §10, etapa 1. Cantidad y precio se escriben en la presentación, como vienen
// en la factura del proveedor; el API convierte a unidades (cantidad × factor,
// precio ÷ factor). Acá sólo se calcula lo que se muestra: unidades, costo
// por unidad (para margen y precio de venta, que son por unidad) y el texto.

import { esFactorEntero, mensajeCompraPorUnidad, unidadesDe as unidadesRedondeadas, unidadesEnteras, mensajeUnidadesNoEnteras } from './presentacionFactor.mjs';

const numero = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * GET /api/product-presentations/:id → la presentación que lleva la línea, o
 * `{ error }` si no se puede comprar con ella. La factura de compra sólo
 * admite CONVERSION de un componente (purchasePresentationService).
 */
export function presentacionDeCompra(full = {}) {
  const componentes = Array.isArray(full?.componentes) ? full.componentes : [];
  if (full?.es_presentacion_compra === false) {
    return { error: `«${full.nombre}» no está habilitada para compra.` };
  }
  if (full?.tipo_presentacion !== 'CONVERSION' || componentes.length !== 1) {
    return { error: `«${full?.nombre || 'La presentación'}» tiene varios productos: cargá cada producto por separado.` };
  }
  const [componente] = componentes;
  const factor = numero(componente.cantidad_operativa);
  if (!factor || factor <= 0 || !componente.producto_id) {
    return { error: `«${full.nombre}» no tiene cantidad por presentación.` };
  }
  // Media caja, media bolsa: se compra por unidad (el API también la rechaza).
  if (!esFactorEntero(factor)) {
    return { error: mensajeCompraPorUnidad({ presentacion: full.nombre }) };
  }
  return {
    presentacion_id: Number(full.presentacion_id),
    nombre: full.nombre,
    factor,
    producto_id: Number(componente.producto_id),
    producto_nombre: componente.producto_nombre || null,
    // RF-13 (H-5): el IVA de la línea es el del producto base.
    tipo_iva: numero(componente.producto_tipo_iva)
  };
}

export function factorDe(presentacion) {
  return numero(presentacion?.factor) || 1;
}

/** Unidades que ingresan al stock. */
export function unidadesDe(cantidad, presentacion) {
  const c = numero(cantidad);
  return c === null ? null : unidadesRedondeadas(c, factorDe(presentacion));
}

/**
 * RF-12 (H-4): aviso de la línea cuando cantidad × factor no da unidades
 * enteras; null si está bien o si la línea es por unidad.
 */
export function avisoCantidad(cantidad, presentacion) {
  const c = numero(cantidad);
  if (!presentacion || c === null || c <= 0) return null;
  if (unidadesEnteras(c, factorDe(presentacion))) return null;
  return mensajeUnidadesNoEnteras({
    presentacion: presentacion.nombre,
    producto: presentacion.producto_nombre || 'el producto',
    cantidad: c,
    factor: factorDe(presentacion)
  });
}

/** Costo por unidad: el precio de la presentación ÷ su factor. */
export function costoPorUnidad(precio, presentacion) {
  const p = numero(precio);
  return p === null ? null : p / factorDe(presentacion);
}

// D4 / RF-14: el costo por unidad con 2 decimales cuando no es entero
// («Gs 1,80»), sin decimales cuando lo es («Gs 5.000»). El API lo guarda así.
export const gsCosto = (n) => {
  const redondeado = Math.round(Number(n) * 100) / 100;
  return Number.isInteger(redondeado)
    ? redondeado.toLocaleString('es-PY')
    : redondeado.toLocaleString('es-PY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
// RF-14: la cantidad tal como se escribió (antes, 0,00015 se veía «0»).
const cant = (n) => Number(n).toLocaleString('es-PY', { maximumFractionDigits: 6 });

/** «2 × Caja x 12 = 24 u. · Gs 5.000 la unidad»; null sin presentación. */
export function textoConversion(cantidad, precio, presentacion) {
  if (!presentacion) return null;
  const unidades = unidadesDe(cantidad, presentacion);
  const costo = costoPorUnidad(precio, presentacion);
  const partes = [];
  if (unidades !== null && numero(cantidad) > 0) {
    partes.push(`${cant(cantidad)} × ${presentacion.nombre} = ${cant(unidades)} u.`);
  } else {
    partes.push(`${presentacion.nombre} = ${cant(factorDe(presentacion))} u.`);
  }
  if (costo !== null && costo > 0) partes.push(`Gs ${gsCosto(costo)} la unidad`);
  return partes.join(' · ');
}

/**
 * RF-2: opciones del selector «Presentación» de una línea, a partir de
 * `GET /api/product-presentations?producto_id=`. Las que no se pueden usar
 * para comprar (combo, media caja, no habilitada) salen deshabilitadas, con
 * su motivo.
 */
export function opcionesPresentacion(lista = []) {
  return (Array.isArray(lista) ? lista : [])
    .filter((p) => p && p.es_presentacion_compra !== false)
    .map((full) => {
      const resultado = presentacionDeCompra(full);
      return resultado.error
        ? { presentacion_id: Number(full.presentacion_id), nombre: full.nombre, deshabilitada: true, motivo: resultado.error }
        : { ...resultado, deshabilitada: false, motivo: null };
    });
}

/**
 * RF-7 / D5: con el aviso `posible_presentacion` del agente y una sola
 * presentación de compra usable, se la propone (no se elige sola).
 */
export function presentacionSugerida(opciones = [], avisos = []) {
  const parece = (avisos || []).some((a) => (a?.codigo || a?.code || a) === 'posible_presentacion');
  if (!parece) return null;
  const usables = (opciones || []).filter((o) => !o.deshabilitada);
  return usables.length === 1 ? usables[0] : null;
}

/** RF-4: nombre sugerido para una presentación nueva («Caja x 12»). */
export function nombreSugerido(factor, tipo = 'Caja') {
  const f = numero(factor);
  return f ? `${tipo} x ${f}` : tipo;
}

/**
 * RF-15: qué dijo el API al rechazar una carga y, si es de una línea, cuál
 * (`linea` desde 1, como la mandó la pantalla). Un rechazo nunca vacía ni
 * bloquea la carga: esto sólo arma el mensaje y la línea a marcar.
 */
export function lineaDelRechazo(error, porDefecto = 'No se pudo guardar.') {
  const data = error?.response?.data || {};
  const sinRespuesta = error && !error.response;
  const mensaje = sinRespuesta
    ? 'No hay conexión con el servidor. La carga sigue en pantalla: revisá la conexión y volvé a confirmar.'
    : (data.message || data.error || error?.message || porDefecto);
  const linea = Number(data.linea);
  return {
    mensaje,
    code: data.code || null,
    linea: Number.isInteger(linea) && linea > 0 ? linea : null,
    producto: data.producto || null
  };
}

/**
 * H-6: una línea de nota de remisión con presentación. La remisión guarda las
 * unidades (`cantidad` = 24) y aparte la presentación y su factor; para pasarla
 * a factura hay que volver a la caja. null si la línea es por unidad.
 */
export function presentacionDeRemision(detalle = {}) {
  if (!detalle?.presentacion_id) return null;
  const unidades = numero(detalle.cantidad);
  const original = numero(detalle.cantidad_presentacion_original);
  const factor = numero(detalle.factor_conversion_aplicado)
    || (unidades && original ? unidades / original : null);
  if (!factor || factor <= 0) return null;
  return {
    presentacion_id: Number(detalle.presentacion_id),
    nombre: detalle.presentacion_nombre || 'Presentación',
    factor,
    producto_id: numero(detalle.producto_id),
    producto_nombre: detalle.nombre_producto || detalle.descripcion || null,
    tipo_iva: numero(detalle.tipo_iva_id)
  };
}

/** H-6: cantidad en presentaciones de una línea de remisión (24 u. ÷ 12 = 2). */
export function cantidadEnPresentacion(detalle = {}, presentacion = null) {
  const original = numero(detalle.cantidad_presentacion_original);
  if (original) return original;
  const unidades = numero(detalle.cantidad) || 0;
  return presentacion ? Math.round((unidades / factorDe(presentacion)) * 1000) / 1000 : unidades;
}
